import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["data", "previousData"];
import { registerQuery, registerMutation, useQuery, useMutation } from 'data-fetching-client';
import { ViewAPI } from 'crm-index-visualization-core/core/data/viewAPI';
import { ViewDraft } from 'crm-index-visualization-core/core/model/viewDraft';
import { VizConfig } from 'crm-index-visualization-core/core/model/viz-config';
import { useCurrentViewId, useCurrentObjectType, useEditHash, useClientStorage, useDraftContext, useBypassStorage } from 'crm-index-visualization-core/core/data/VizCoreProvider';
import { useMemo } from 'react';
import { mapIfExistsOrThrow } from 'crm-index-visualization-core/core/error/errorUtils';
import { useAutoBatch } from 'crm-index-visualization-core/core/hooks/useAutoBatch';
import { currentViewQuery } from './view-hooks';
export const getVizConfigQuery = registerQuery({
  fieldName: 'vizcoreVizConfig',
  fetcher: ({
    editHash,
    clientStorage,
    vizType,
    version,
    id,
    objectTypeId
  }) => {
    // Detect bypass mode from the hash key or bypass storage flag
    const isBypassMode = editHash === 'bypass-draft-active';
    if (isBypassMode) {
      // For bypass mode, fetch from backend and return default config
      return ViewAPI.getView(id, objectTypeId, clientStorage).then(beView => VizConfig.fromBEView(beView, vizType, version));
    }
    // Try draft first, then fall back to BE
    const fromDraft = ViewDraft.fromClientCache(editHash !== null && editHash !== void 0 ? editHash : '', clientStorage).then(draft => mapIfExistsOrThrow(draft.vizConfigHashesById[`${vizType}@${version}`], h => VizConfig.fromClientCache(h, clientStorage), new Error('No viz config hash found')));
    return fromDraft.catch(async () => {
      // Fall back to fetching from BE
      const beView = await ViewAPI.getView(id, objectTypeId, clientStorage);
      return VizConfig.fromBEView(beView, vizType, version);
    });
  },
  args: ['editHash', 'clientStorage', 'vizType', 'version', 'id', 'objectTypeId']
});
export const saveVizConfigDraftMutation = registerMutation({
  fieldName: 'vizcoreSaveVizConfigDraft',
  fetcher: async ({
    vizConfig,
    editHash,
    clientStorage,
    viewId,
    bypassStorage
  }) => {
    var _ref;
    // QUICK FIX: Skip storage operations to prevent rollbacks
    if (bypassStorage) {
      return {
        vizConfigHash: 'bypass-vizConfig',
        viewDraftHash: editHash || 'bypass-draft-active',
        vizConfig
      };
    }
    // Save viz config and get hash
    const vizConfigHash = await vizConfig.toClientCache(clientStorage);

    // Load and update view draft reference
    const existingDraft = await ViewDraft.fromClientCache(editHash !== null && editHash !== void 0 ? editHash : '',
    // there is no empty string hash, so effectively existingDraft will be null
    clientStorage).catch(() => null);

    // Create or update view draft with new viz config hash
    const baseDraft = existingDraft !== null && existingDraft !== void 0 ? existingDraft : new ViewDraft({
      viewId
    });
    const updatedDraft = baseDraft.with({
      vizConfigHashesById: Object.assign({}, (_ref = existingDraft === null || existingDraft === void 0 ? void 0 : existingDraft.vizConfigHashesById) !== null && _ref !== void 0 ? _ref : {}, {
        [`${vizConfig.vizType}@${vizConfig.version}`]: vizConfigHash
      })
    });

    // Save updated view draft
    const viewDraftHash = await updatedDraft.toClientCache(clientStorage);
    return {
      vizConfigHash,
      viewDraftHash,
      vizConfig
    };
  },
  args: ['vizConfig', 'editHash', 'clientStorage', 'viewId', 'bypassStorage']
});
/**
 * Hook to fetch and manage visualization configuration data.
 * Transparently handles draft data from localStorage if an editHash is present.
 *
 * @param options - Restricted query options including vizType and version in variables
 * @throws {Error} If used outside of a VizCoreProvider
 * @returns {Object} Query result with transformed viz config data
 *   - data: VizConfig | null - The current viz config if available
 *   - loading: boolean - Whether the query is loading
 *   - error: ApolloError | undefined - Any query errors
 *
 * @deprecated useVizSettings instead
 */
export function useCurrentVizConfig(options) {
  const [editHash] = useEditHash();
  const clientStorage = useClientStorage();
  const bypassStorage = useBypassStorage();
  const viewId = useCurrentViewId();
  const objectTypeId = useCurrentObjectType();
  const _useQuery = useQuery(getVizConfigQuery, Object.assign({}, options, {
      onCompleted: options.onSuccess,
      variables: {
        editHash,
        clientStorage,
        vizType: options.variables.vizType,
        version: options.variables.version,
        id: viewId,
        objectTypeId,
        bypassStorage
      }
    })),
    {
      data,
      previousData
    } = _useQuery,
    rest = _objectWithoutPropertiesLoose(_useQuery, _excluded);
  return useMemo(() => {
    return Object.assign({
      data: data !== null && data !== void 0 ? data : previousData
    }, rest);
  }, [data, previousData, rest]);
}

/**
 * Hook to save visualization configuration drafts with auto-batching
 * Multiple save operations within the debounce window will be batched into a single save
 *
 * @param options - Mutation options including onSuccess and onError callbacks
 * @param autoBatch - Time in milliseconds to debounce save operations, default 100ms
 * @throws {Error} if used outside VizCoreProvider
 * @returns [saveFn, result, flushFn] - Where:
 *   - saveFn: Accepts either a VizConfig object directly or the legacy {variables: {vizConfig}} structure
 *   - result: The mutation result
 *   - flushFn: Function to manually flush pending changes
 *
 * @example
 * // Example 1: Direct VizConfig usage
 * const [saveVizConfig] = useSaveVizConfigDraft();
 * saveVizConfig(vizConfig.with({ columns: ['name', 'email'] }));
 *
 * // Example 2: Legacy format with variables wrapper
 * const [saveVizConfig] = useSaveVizConfigDraft();
 * saveVizConfig({
 *   variables: {
 *     vizConfig: vizConfig.with({ columns: ['name', 'email'] })
 *   }
 * });
 *
 * // Example 3: Using the updater function pattern
 * const [saveVizConfig] = useSaveVizConfigDraft();
 * saveVizConfig(prev => prev.with({
 *   columns: [...prev.columns, 'email']
 * }));
 *
 * @deprecated useMutateVizSettings instead
 */
export function useSaveVizConfigDraft(options = {
  autoBatch: 100
}) {
  const [draftContext, onDraftContextChange] = useDraftContext();
  const bypassStorage = useBypassStorage();
  const viewId = useCurrentViewId();
  const clientStorage = useClientStorage();
  const [mutate, result] = useMutation(saveVizConfigDraftMutation, Object.assign({}, options, {
    optimisticResponse: vars => {
      return {
        vizcoreSaveVizConfigDraft: {
          vizConfigHash: 'bypass-vizConfig',
          viewDraftHash: vars.editHash,
          vizConfig: vars.vizConfig
        }
      };
    },
    update: (cache, {
      data
    }) => {
      var _cache$readQuery;
      const view = (_cache$readQuery = cache.readQuery({
        query: currentViewQuery,
        variables: {
          viewId: draftContext.viewId,
          objectTypeId: draftContext.objectTypeId,
          clientStorage
        }
      })) === null || _cache$readQuery === void 0 ? void 0 : _cache$readQuery.vizcoreCurrentView;
      if (data !== null && data !== void 0 && data.vizcoreSaveVizConfigDraft && view) {
        var _data$vizcoreSaveVizC, _data$vizcoreSaveVizC2;
        cache.writeQuery({
          query: getVizConfigQuery,
          variables: {
            editHash: data.vizcoreSaveVizConfigDraft.viewDraftHash,
            clientStorage,
            vizType: (_data$vizcoreSaveVizC = data.vizcoreSaveVizConfigDraft.vizConfig) === null || _data$vizcoreSaveVizC === void 0 ? void 0 : _data$vizcoreSaveVizC.vizType,
            version: (_data$vizcoreSaveVizC2 = data.vizcoreSaveVizConfigDraft.vizConfig) === null || _data$vizcoreSaveVizC2 === void 0 ? void 0 : _data$vizcoreSaveVizC2.version,
            id: draftContext.viewId,
            objectTypeId: draftContext.objectTypeId
          },
          data: {
            vizcoreVizConfig: data.vizcoreSaveVizConfigDraft.vizConfig
          }
        });
      }
    },
    onCompleted(data) {
      var _options$onSuccess;
      const {
        vizcoreSaveVizConfigDraft: {
          viewDraftHash
        }
      } = data;

      // QUICK FIX: Skip edit hash update to prevent rollbacks
      if (bypassStorage) {
        if (draftContext.editHash === null) {
          onDraftContextChange(Object.assign({}, draftContext, {
            editHash: viewDraftHash
          }));
        }
      } else {
        onDraftContextChange(Object.assign({}, draftContext, {
          editHash: viewDraftHash
        }));
      }
      (_options$onSuccess = options.onSuccess) === null || _options$onSuccess === void 0 || _options$onSuccess.call(options, data);
    }
  }));

  // Create a function to perform the actual mutation
  const performMutation = useMemo(() => {
    return vizConfig => {
      return mutate({
        variables: {
          vizConfig,
          editHash: draftContext.editHash,
          clientStorage,
          viewId,
          bypassStorage
        }
      });
    };
  }, [mutate, draftContext.editHash, clientStorage, viewId, bypassStorage]);

  // Use auto-batching to debounce multiple save operations
  const [queueSave, flushSave] = useAutoBatch(performMutation, options.autoBatch);
  return useMemo(() => {
    function wrappedMutate(params) {
      const vizConfigValue = 'variables' in params ? params.variables.vizConfig : params;

      // Queue the save operation, which will be debounced
      return typeof vizConfigValue === 'function' ? queueSave(vizConfigValue) : queueSave(() => vizConfigValue);
    }

    // Return the wrapped mutation function, the result, and a flush function
    // to manually trigger a save if needed
    return [wrappedMutate, result, flushSave];
  }, [queueSave, flushSave, result]);
}